import React, { useState, useEffect, useRef } from 'react';
import { useConfirm, useAlert } from '../components/ConfirmModal';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import API from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { trackStoryApproved, trackStoryRejected } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import { isViaCoParent } from '../utils/entitlements';

const ApprovalQueuePage = () => {
  const { darkMode: dm } = useTheme();
  const { user } = useAuth();
  // approveStory and rejectStory both 403 for co-parents. A co-parent with
  // can_generate still lands here after generating, so show the queue read-only
  // and say who has to act, rather than offering two buttons that both fail.
  const canReview = !isViaCoParent(user);
  const confirm = useConfirm();
  const alert = useAlert();
  const [stories, setStories]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState(null);  // story._id being previewed
  const readMarked                = useState(() => new Set())[0];
  const [acting, setActing]       = useState({});
  const [searchParams]            = useSearchParams();
  const justAdded                 = searchParams.get('new') === 'true';
  const pollRef                   = useRef(null);

  const fetchQueue = (silent = false) => {
    if (!silent) setLoading(true);
    API.get('/stories/approval/queue')
      .then(res => {
        setStories(res.data.stories);
        // If an expanded story just became ready, update preview
        setExpanded(prev => {
          if (!prev) return prev;
          const updated = res.data.stories.find(s => s._id === prev);
          return updated ? prev : null;
        });
      })
      .catch(console.error)
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { fetchQueue(); }, []); // eslint-disable-line

  // Auto-refresh every 3s while any story is still being generated
  useEffect(() => {
    const hasGenerating = stories.some(s => s.generationStatus === 'pending');
    if (hasGenerating && !pollRef.current) {
      pollRef.current = setInterval(() => {
        fetchQueue(true); // silent refresh (no loading spinner)
      }, 3000);
    }
    if (!hasGenerating && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {};
  }, [stories]); // eslint-disable-line

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleApprove = async (id) => {
    setActing(a => ({ ...a, [id]: 'approving' }));
    try {
      await API.put(`/stories/approval/${id}/approve`);
      trackStoryApproved();
      setStories(s => s.filter(st => st._id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err) {
      await alert({ title: 'Error', message: err.response?.data?.message || 'Failed to approve story.', danger: true });
    } finally {
      setActing(a => { const n = { ...a }; delete n[id]; return n; });
    }
  };

  const handleReject = async (id) => {
    const ok = await confirm({ title: 'Delete story?', message: 'This story will be permanently deleted and cannot be recovered.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setActing(a => ({ ...a, [id]: 'rejecting' }));
    try {
      await API.delete(`/stories/approval/${id}/reject`);
      trackStoryRejected();
      setStories(s => s.filter(st => st._id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err) {
      await alert({ title: 'Error', message: err.response?.data?.message || 'Failed to reject story.', danger: true });
    } finally {
      setActing(a => { const n = { ...a }; delete n[id]; return n; });
    }
  };

  const readyStories      = stories.filter(s => s.generationStatus === 'ready');
  const generatingStories = stories.filter(s => s.generationStatus === 'pending' || s.generationStatus === 'failed');
  const preview           = readyStories.find(s => s._id === expanded);

  return (
    <DashboardLayout title="Approval Queue">

      {/* Header */}
      <div className="rounded-4 p-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{background:'var(--blue-section)', border:'1px solid var(--border-blue)'}}>
        <div>
          <h4 className="fw-bold mb-1 d-flex align-items-center gap-2" style={{color:'var(--text-blue)', fontFamily:"'Fredoka One',cursive"}}>
            <i className="bi bi-eye-fill" style={{color:'#2563eb'}}></i> Approval Queue
            {readyStories.length > 0 && (
              <span className="badge rounded-pill text-white ms-1" style={{background:'#2563eb', fontSize:'14px'}}>
                {readyStories.length}
              </span>
            )}
          </h4>
          <p className="mb-0 small" style={{color:'var(--text-blue2)'}}>
            {canReview
              ? 'Review each story before it appears in the library. Approve to publish, or reject to discard.'
              : 'These stories are waiting for the primary parent to approve them. You can read them here in the meantime.'}
          </p>
        </div>
        <Link to="/dashboard/children" className="btn btn-sm rounded-pill px-3"
          style={{background:'#2563eb', color:'#fff', fontWeight:600}}>
          <i className="bi bi-gear me-1"></i>Manage Queue Settings
        </Link>
      </div>

      {/* Credit policy notice */}
      {readyStories.length > 0 && (
        <div className="d-flex align-items-start gap-3 p-3 mb-4 rounded-3"
          style={{background:'var(--yellow-section)', border:'1px solid var(--border-yellow)'}}>
          <i className="bi bi-exclamation-triangle-fill flex-shrink-0 mt-1" style={{color:'#d97706', fontSize:'16px'}}></i>
          <div className="small" style={{color:'var(--text-orange2)'}}>
            <strong>How credits work in the queue:</strong>{' '}
            Rejecting a story <strong>without opening the preview</strong> refunds your story credit in full.
            Once you click <strong>Read</strong> and view the full story, the credit is used — rejecting after reading will <strong>not</strong> give a refund.
          </div>
        </div>
      )}

      {/* New story toast */}
      {justAdded && (readyStories.length > 0 || generatingStories.length > 0) && (
        <div className="alert d-flex align-items-center gap-3 mb-4 rounded-3"
          style={{background:'var(--blue-section-2)', border:'1px solid var(--border-blue)', color:'var(--text-blue)'}}>
          <i className="bi bi-info-circle-fill fs-5" style={{color:'#2563eb'}}></i>
          <div>
            <strong>Story added to queue.</strong>{' '}
            {generatingStories.length > 0
              ? 'It\'s being crafted now — review will be available in a moment.'
              : 'Review it below before it goes to the library.'}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{color:'#2563eb', width:'3rem', height:'3rem'}}></div>
          <p className="text-muted mt-3">Loading queue…</p>
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-5">
          <div style={{fontSize:'80px', marginBottom:'16px'}}>✅</div>
          <h4 className="fw-bold mb-2" style={{color:'#1e3a8a'}}>Queue is empty</h4>
          <p className="text-muted mb-4" style={{maxWidth:'420px', margin:'0 auto 24px'}}>
            All stories have been reviewed. New stories for children with Approval Queue enabled will appear here.
          </p>
          <Link to="/dashboard/generate" className="btn btn-dream rounded-pill px-5 fw-semibold">
            <i className="bi bi-magic me-2"></i>Generate a Story
          </Link>
        </div>
      ) : (
        <>
          {/* ── Stories still being generated ── */}
          {generatingStories.length > 0 && (
            <div className="mb-4">
              <p className="small fw-semibold mb-2" style={{color:'#6b7280'}}>
                <i className="bi bi-hourglass-split me-1"></i>Being crafted…
              </p>
              <div className="d-flex flex-column gap-2">
                {generatingStories.map(story => {
                  const child = story.child;
                  const failed = story.generationStatus === 'failed';
                  return (
                    <div key={story._id} className="card border-0 shadow-sm"
                      style={{borderRadius:'16px', overflow:'hidden', opacity: failed ? 0.7 : 1}}>
                      <div className="d-flex align-items-center gap-3 p-3">
                        <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                          style={{width:'60px', height:'60px', background:'var(--blue-section)'}}>
                          {failed
                            ? <i className="bi bi-exclamation-circle-fill text-danger fs-4"></i>
                            : <div className="spinner-border spinner-border-sm" style={{color:'#2563eb'}}></div>}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold mb-1" style={{color:'var(--text-blue)', fontSize:'14px'}}>
                            {failed ? 'Story generation failed' : 'Crafting your story…'}
                          </div>
                          <div className="text-muted small">
                            {child?.avatar} {child?.name}, age {child?.age}
                            {failed
                              ? ' — this story\'s credit has been refunded automatically'
                              : ' — this will be ready to review in a moment'}
                          </div>
                        </div>
                        {failed && (
                          <button className="btn btn-sm rounded-pill px-3 flex-shrink-0"
                            style={{background:'var(--reject-bg)', color:'var(--reject-fg)', fontSize:'12px', fontWeight:600}}
                            onClick={() => handleReject(story._id)}>
                            <i className="bi bi-x-circle me-1"></i>Remove
                          </button>
                        )}
                      </div>
                      {!failed && (
                        <div className="progress mx-3 mb-3" style={{height:'4px', borderRadius:'2px'}}>
                          <div className="progress-bar progress-bar-striped progress-bar-animated"
                            style={{width:'100%', background:'#2563eb'}}></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Stories ready to review ── */}
          {readyStories.length > 0 && (
            <div className="row g-0">
              {/* Story list */}
              <div className={`${expanded ? 'col-lg-5' : 'col-12'}`} style={{transition:'all 0.2s'}}>
                <div className="d-flex flex-column gap-3">
                  {readyStories.map(story => {
                    const child = story.child;
                    const isActing = !!acting[story._id];
                    const isOpen = expanded === story._id;

                    return (
                      <div key={story._id}
                        className="card border-0 shadow-sm"
                        style={{borderRadius:'16px', overflow:'hidden',
                          outline: isOpen ? '2px solid #2563eb' : '2px solid transparent',
                          transition:'outline 0.15s'}}>

                        <div className="d-flex" style={{minHeight:'100px'}}>
                          {/* Illustration thumbnail */}
                          <div style={{width:'90px', minWidth:'90px', background:'var(--blue-section)', position:'relative', flexShrink:0}}>
                            {story.illustrationUrl ? (
                              <img src={story.illustrationUrl} alt=""
                                style={{width:'100%', height:'100%', objectFit:'cover'}} />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center h-100"
                                style={{fontSize:'32px'}}>🌙</div>
                            )}
                            {story.illustrationStatus === 'generating' && (
                              <div className="d-flex align-items-center justify-content-center"
                                style={{position:'absolute',inset:0,background: dm ? 'rgba(15,11,30,0.7)' : 'rgba(255,255,255,0.7)'}}>
                                <div className="spinner-border spinner-border-sm" style={{color:'#2563eb'}}></div>
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="p-3 flex-grow-1">
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                              <h6 className="fw-bold mb-0 text-truncate" style={{color:'var(--text-blue)', maxWidth:'220px'}}>
                                {story.title}
                              </h6>
                              <span className="badge rounded-pill flex-shrink-0"
                                style={{background:'var(--badge-yellow-bg)', color:'var(--badge-yellow-fg)', fontSize:'10px', fontWeight:600}}>
                                Pending
                              </span>
                            </div>

                            <div className="text-muted small mb-2">
                              {child?.avatar} {child?.name}, age {child?.age}
                              {story.isMultiChild && story.multiChildIds?.length > 1 && (
                                <span className="ms-1">+ {story.multiChildIds.length - 1} sibling{story.multiChildIds.length > 2 ? 's' : ''}</span>
                              )}
                              {story.readingTime && <span className="ms-2">· {story.readingTime} min</span>}
                            </div>

                            {story.moralLesson && (
                              <p className="small fst-italic mb-2 text-truncate" style={{color:'#6f42c1', maxWidth:'260px'}}>
                                "{story.moralLesson}"
                              </p>
                            )}

                            <div className="d-flex gap-2 flex-wrap">
                              <button className="btn btn-sm rounded-pill px-3"
                                style={{background:'var(--read-bg)', color:'var(--read-fg)', fontSize:'12px', fontWeight:600}}
                                title={!isOpen && !readMarked.has(story._id) ? 'Opening the preview uses your story credit. Rejecting after reading will not refund it.' : undefined}
                                onClick={() => {
                                  if (!isOpen && !readMarked.has(story._id)) {
                                    API.put(`/stories/${story._id}/read`).catch(() => {});
                                    readMarked.add(story._id);
                                  }
                                  setExpanded(isOpen ? null : story._id);
                                }}>
                                <i className={`bi ${isOpen ? 'bi-chevron-left' : 'bi-eye'} me-1`}></i>
                                {isOpen ? 'Close' : 'Read'}
                                {!isOpen && !readMarked.has(story._id) && (
                                  <i className="bi bi-exclamation-circle ms-1" style={{color:'#d97706', fontSize:'11px'}}></i>
                                )}
                              </button>
                              {canReview && (
                              <button className="btn btn-sm rounded-pill px-3"
                                style={{background:'var(--approve-bg)', color:'var(--approve-fg)', fontSize:'12px', fontWeight:600}}
                                onClick={() => handleApprove(story._id)}
                                disabled={isActing}>
                                {acting[story._id] === 'approving'
                                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Approving…</>
                                  : <><i className="bi bi-check2-circle me-1"></i>Approve</>}
                              </button>
                              )}
                              {canReview && (
                              <button className="btn btn-sm rounded-pill px-3"
                                style={{background:'var(--reject-bg)', color:'var(--reject-fg)', fontSize:'12px', fontWeight:600}}
                                onClick={() => handleReject(story._id)}
                                disabled={isActing}>
                                {acting[story._id] === 'rejecting'
                                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Deleting…</>
                                  : <><i className="bi bi-x-circle me-1"></i>Reject</>}
                              </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Story preview panel */}
              {preview && (
                <div className="col-lg-7 ps-lg-4 mt-4 mt-lg-0">
                  <div className="card border-0 shadow-sm sticky-top" style={{borderRadius:'20px', top:'80px'}}>

                    {preview.illustrationUrl && (
                      <img src={preview.illustrationUrl} alt={preview.title}
                        style={{width:'100%', height:'220px', objectFit:'cover', borderRadius:'20px 20px 0 0'}} />
                    )}

                    <div className="p-4">
                      <h5 className="fw-bold mb-1" style={{color:'var(--text-blue)', fontFamily:"'Fredoka One',cursive", fontSize:'20px'}}>
                        {preview.title}
                      </h5>
                      <p className="text-muted small mb-3">
                        {preview.child?.avatar} {preview.child?.name}
                        {preview.readingTime && ` · ${preview.readingTime} min read`}
                        {preview.language && preview.language !== 'en' && ` · ${preview.language.toUpperCase()}`}
                      </p>

                      <div style={{maxHeight:'340px', overflowY:'auto', fontSize:'15px', color:'var(--text-main)', lineHeight:'1.85', fontFamily:"Georgia, serif"}}>
                        {preview.content.split('\n\n').map((para, i) => (
                          <p key={i} style={{marginBottom:'14px'}}>{para}</p>
                        ))}
                      </div>

                      {preview.moralLesson && (
                        <div className="mt-3 p-3 rounded-3 text-center"
                          style={{background:'var(--purple-section)', border:'2px dashed #c9a8ff'}}>
                          <div style={{fontSize:'20px', marginBottom:'4px'}}>💫</div>
                          <p className="small fw-bold mb-1" style={{color:'var(--text-main)'}}>Tonight's lesson</p>
                          <p className="small fst-italic mb-0" style={{color:'#6f42c1'}}>"{preview.moralLesson}"</p>
                        </div>
                      )}

                      <div className="d-flex gap-2 mt-4">
                        <button className="btn rounded-pill fw-bold flex-grow-1"
                          style={{background:'linear-gradient(135deg,#16a34a,#15803d)', color:'#fff'}}
                          onClick={() => handleApprove(preview._id)}
                          disabled={!!acting[preview._id]}>
                          {acting[preview._id] === 'approving'
                            ? <><span className="spinner-border spinner-border-sm me-2"></span>Approving…</>
                            : <><i className="bi bi-check2-circle me-2"></i>Approve — Add to Library</>}
                        </button>
                        <button className="btn rounded-pill px-3"
                          style={{background:'var(--reject-bg)', color:'var(--reject-fg)', fontWeight:600}}
                          onClick={() => handleReject(preview._id)}
                          disabled={!!acting[preview._id]}>
                          <i className="bi bi-x-circle me-1"></i>Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default ApprovalQueuePage;
